import React, {useCallback, useRef} from "react";
import {Box, Card, CircularProgress, Paper, Skeleton, Typography} from "@mui/material";
import {ReactSortable} from "react-sortablejs";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ArrowBackRounded} from "@mui/icons-material";
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
import {useBizFileUrlsResolver} from "../../../../../../../hooks/use-biz-file-urls-resolver";

const IndustryManagementViewWithoutAccessColumn = ({moveIndustry, setIndustryCloned, ...props}) => {
    const choseIndustry = useRef(null);
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);

    /**
     * Set the industry to move
     * @type {(function(*, *): void)|*}
     */
    const onDragStarted = useCallback((e) => {
        setIndustryCloned(true);
        const industryId = e.clone.dataset.id;
        if (!industryId)
            return;
        choseIndustry.current = props.data.find(e => e.id === industryId);
    }, [setIndustryCloned, props.data])

    /**
     * finds the task to move and calls the Move task
     */
    const onMoved = useCallback((e) => {
        setIndustryCloned(false);
        choseIndustry.current = null;
        if (`industry-${props.id}` === e.from.id && e.to.id !== e.from.id) {
            const industry = props.data[e.oldDraggableIndex];
            if (!industry)
                return;
            void moveIndustry(industry, e);
        }
    }, [props.data, moveIndustry, props.id, setIndustryCloned])

    /**
     * Add industry to access list
     * @type {(function(*): void)|*}
     */
    const addIndustry = useCallback((industry) => {
        void moveIndustry(industry, {}, true, props.data.findIndex(e => e.id === industry.id));
    }, [moveIndustry, props.data])

    return (
        <>
            <Paper
                sx={({
                    width: {xs: '100%', md: 'calc(40% - 4px)', lg: 'calc(30% - 4px)'},
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: 2,
                    gap: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'background.tertiary',
                    position: 'sticky',
                    top: 0,
                })}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "space-between"}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        {props.title}
                    </Typography>
                </Box>
                <Box sx={{
                    overflowY: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    paddingBlock: 1,
                    paddingInlineEnd: 0.5,
                }}>
                    <ReactSortable
                        id={`industry-${props.id}`}
                        setList={() => void 0}
                        list={props.data}
                        onEnd={onMoved}
                        onStart={onDragStarted}
                        group={{
                            name: 'industries',
                            put: () => false,
                            checkPut: () => true,
                        }}
                        animation={210}
                        dragoverBubble
                        disabled={props.loading || props.movingIndustry}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            gap: 15,
                            height: 'fit-content',
                        }}
                    >
                        {
                            props.data.map(industry => (
                                <Card
                                    data-id={industry.id}
                                    key={industry.id}
                                    variant={'outlined'}
                                    sx={{
                                        padding: 1,
                                        color: 'text.primary',
                                        borderColor: 'divider',
                                        cursor: 'move',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <IconButton
                                        color={'paper.primary'}
                                        size={'small'}
                                        component={'span'}
                                        disabled={props.loading || props.movingIndustry}
                                        onPressed={() => addIndustry(industry)}
                                    >
                                        {
                                            choseIndustry.current?.id === industry.id
                                                ? <CircularProgress size={20}/>
                                                : <ArrowBackRounded className={'icon small'}/>
                                        }
                                    </IconButton>
                                    {
                                        industry.iconURL &&
                                        <LazyImage
                                            src={bizFileUrls.getFilePreviewUrl(industry.iconURL)}
                                            height={24}
                                            width={24}
                                            style={{borderRadius: 8}}
                                        />
                                    }
                                    <Typography variant={'body2'} fontWeight={600}>
                                        {industry.title}
                                    </Typography>
                                </Card>
                            ))
                        }
                    </ReactSortable>
                    {
                        props.loading &&
                        Array.from({length: 10}).map((_, key) => (
                            <Skeleton
                                key={`loading-${key}`}
                                width={'100%'}
                                sx={{minHeight: 48, transformOrigin: 'none', transform: 'none'}}
                            />
                        ))
                    }
                    {
                        props.data.length === 0 && !props.loading &&
                        <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 20}}>
                            There are no industries to add to your organization.
                        </Typography>
                    }
                </Box>
            </Paper>
        </>
    );
}

export default IndustryManagementViewWithoutAccessColumn;
